import { useState } from 'react'
import { ApiError, api, fmtTime, type Me } from '../api'
import { useT } from '../lang'
import { invalidate, useApi } from '../useApi'
import { Link } from '../router'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Field, Rows, StateBadge } from '../components/bits'

interface Mic {
  microphone_id: string
  type: string | null
  serial: string | null
  sensitivity_dbv_upa: number | null
  max_depth_m: number | null
  calibrations: number
  mission_id?: string | null
  site_id?: string | null
  site_name?: string | null
  label?: string | null
  /** Non-null means the unit records without a compute. Worth showing here
   *  because a list mixing sensors and recorders otherwise gives no clue why
   *  half of them can never report an absolute level. */
  recorder_model?: string | null
}

export function Hydrophones() {
  const t = useT()
  // Deployed only by default. A retired sensor is kept for ever -- a 2026
  // recording must stay interpretable in 2031, and that needs the certificate
  // current when it was made -- but listing retired hardware alongside the one
  // hydrophone actually in the water answers the wrong question.
  const [all, setAll] = useState(false)
  const q = useApi<{ items: Mic[]; total: number; all_total: number }>(
    `/v1/microphones?limit=200${all ? '' : '&deployed=true'}`,
  )
  const items = q.data?.items ?? []
  const hidden = (q.data?.all_total ?? 0) - (all ? 0 : q.data?.total ?? 0)

  return (
    <>
      <PageHead
        title={t('Hydrophones')}
      />
      <ErrorBox error={q.error} />

      <div className="card flush">
        {q.cold ? (
          <Rows />
        ) : items.length === 0 ? (
          <Empty title="No hydrophones yet">
            One is added for you when you register a hydrophone with its serial
            number.
          </Empty>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('Serial')}</th>
                <th>{t('Model')}</th>
                <th>In the water at</th>
                <th className="r">Sensitivity</th>
                <th className="r">Certificates</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.microphone_id}>
                  <td data-l="Serial">
                    <Link className="rowlink" to={`/calibration/${encodeURIComponent(m.microphone_id)}`}>
                      {m.serial ?? m.microphone_id}
                    </Link>
                    <div className="mono faint">{m.label ?? m.microphone_id}</div>
                  </td>
                  {/* The recorder model wins when there is one: for a
                      self-recording unit that is the answer to "what is this",
                      and `type` describes only the element inside it. */}
                  <td data-l="Model" className="faint">
                    {m.recorder_model ?? m.type ?? '—'}
                  </td>
                  <td data-l="In the water at">
                    {m.site_id ? (
                      <Link to={`/sites/${encodeURIComponent(m.site_id)}`}>
                        {m.site_name ?? m.site_id}
                      </Link>
                    ) : (
                      <span className="faint">retired</span>
                    )}
                  </td>
                  <td data-l="Sensitivity" className="r num">
                    {m.sensitivity_dbv_upa != null
                      ? `${m.sensitivity_dbv_upa} dBV`
                      : <span className="faint">unknown</span>}
                  </td>
                  <td data-l="Certificates" className="r num">
                    {m.calibrations || <span className="faint">none</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(hidden > 0 || all) && (
        <button className="btn ghost sm" style={{ marginTop: 14 }} onClick={() => setAll(!all)}>
          {all ? 'Show only what is deployed' : `Show ${hidden} retired hydrophone${hidden === 1 ? '' : 's'}`}
        </button>
      )}
    </>
  )
}

interface MicDetail extends Mic {
  notes: string | null
  calibrations_list?: never
}

interface Cal {
  id: number
  sensitivity_dbv_upa: number
  method: string | null
  certificate_ref: string | null
  valid_from: string
  valid_to: string | null
}

export function HydrophonePage({ id, me }: { id: string; me: Me }) {
  const q = useApi<
    Omit<MicDetail, 'calibrations'> & {
      calibrations: Cal[]
      missions: { mission_id: string; site_id: string | null; state: string; started_at: string; ended_at: string | null }[]
    }
  >(`/v1/microphones/${encodeURIComponent(id)}`)
  const m = q.data

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!m) return null

  return (
    <>
      <PageHead
        title={m.serial ?? m.microphone_id}
        lead={<>{m.type ?? 'unknown model'} · <span className="mono">{m.microphone_id}</span></>}
      />

      <div className="stack">
        <MicDetails m={m} me={me} path={`/v1/microphones/${encodeURIComponent(id)}`}
                    onSaved={() => { invalidate(`/v1/microphones/${encodeURIComponent(id)}`); q.reload() }} />

        <div className="card flush">
          <div className="card-head"><h2>Calibration history</h2></div>
          {/* Versioned with validity dates (R2.4) so a recording is always
              interpreted with the certificate that was current when it was
              made. Re-calibrating adds a row here; it never rewrites what last
              year's data meant. */}
          {m.calibrations.length === 0 ? (
            <Empty title="No certificate on file">
              Without a dated sensitivity this hydrophone's recordings stay
              site-relative, and levels cannot be stated in dB re 1 µPa.
            </Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th className="r">Sensitivity</th>
                  <th>Method</th>
                  <th>Certificate</th>
                  <th>Valid from</th>
                  <th>Valid to</th>
                </tr>
              </thead>
              <tbody>
                {m.calibrations.map((c) => (
                  <tr key={c.id}>
                    <td data-l="Sensitivity" className="r num">{c.sensitivity_dbv_upa} dBV re 1 µPa</td>
                    <td data-l="Method" className="faint">{c.method ?? '—'}</td>
                    <td data-l="Certificate" className="faint">{c.certificate_ref ?? '—'}</td>
                    <td data-l="Valid from" className="num faint">{fmtTime(c.valid_from)}</td>
                    <td data-l="Valid to" className="num faint">
                      {c.valid_to ? fmtTime(c.valid_to) : <span className="badge on">current</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card flush">
          <div className="card-head"><h2>Where it has been</h2></div>
          {m.missions.length === 0 ? (
            <Empty title="Never deployed" />
          ) : (
            <table className="tbl">
              <thead>
                <tr><th>Mission</th><th>Site</th><th>State</th><th>From</th></tr>
              </thead>
              <tbody>
                {m.missions.map((x) => (
                  <tr key={x.mission_id}>
                    <td data-l="Mission">
                      <Link className="rowlink" to={`/missions/${encodeURIComponent(x.mission_id)}`}>
                        {x.mission_id}
                      </Link>
                    </td>
                    <td data-l="Site" className="faint">
                      {x.site_id ? (
                        <Link to={`/sites/${encodeURIComponent(x.site_id)}`}>{x.site_id}</Link>
                      ) : '—'}
                    </td>
                    <td data-l="State"><StateBadge state={x.state} /></td>
                    <td data-l="From" className="num faint">{fmtTime(x.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}


/** Model, serial and depth rating, and a way to correct them.
 *
 *  These are the fields a person needs before they can say which hydrophone
 *  they are holding, and until now nothing in the interface could write them:
 *  the PATCH endpoint accepted type, serial, max_depth_m and notes, and no
 *  screen offered any of them. A registry you can only read is a registry that
 *  goes stale the first time somebody swaps a sensor.
 *
 *  Sensitivity is deliberately not here. It is versioned in mic_calibrations
 *  with validity dates (R2.4), so changing it means adding a dated certificate
 *  rather than editing a number -- editing it in place would silently rewrite
 *  what every past recording means.
 */
function MicDetails({
  m, me, path, onSaved,
}: {
  m: { type: string | null; serial: string | null; max_depth_m: number | null; notes: string | null }
  me: Me
  path: string
  onSaved: () => void
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [type, setType] = useState('')
  const [serial, setSerial] = useState('')
  const [depth, setDepth] = useState('')
  const [notes, setNotes] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const start = () => {
    setType(m.type ?? '')
    setSerial(m.serial ?? '')
    setDepth(m.max_depth_m?.toString() ?? '')
    setNotes(m.notes ?? '')
    setErr(null)
    setEditing(true)
  }

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      // Empty means "clear it", not "leave it": a serial typed by mistake has
      // to be removable, and a field that can only ever be set is a field that
      // records the first guess for ever.
      await api.patch(path, {
        type: type.trim() || null,
        serial: serial.trim() || null,
        max_depth_m: depth.trim() === '' ? null : Number(depth),
        notes: notes.trim() || null,
      })
      setEditing(false)
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 className="display" style={{ fontSize: 17 }}>{t('Details')}</h2>
      </div>

      <dl className="dl">
        <dt>{t('Model')}</dt>
        <dd>{m.type ?? <span className="faint">unknown</span>}</dd>
        <dt>{t('Serial')}</dt>
        <dd className="mono">{m.serial ?? <span className="faint">unknown</span>}</dd>
        <dt>Depth rating</dt>
        <dd className="num">
          {m.max_depth_m != null ? `${m.max_depth_m} m` : <span className="faint">unknown</span>}
        </dd>
        <dt>{t('Notes')}</dt>
        <dd>{m.notes || <span className="faint">—</span>}</dd>
      </dl>

      {me.can_write && !editing && (
        <button className="btn sm" style={{ marginTop: 16 }} onClick={start}>
          Edit hydrophone
        </button>
      )}

      {editing && (
        <div className="stack" style={{ marginTop: 16, gap: 14 }}>
          <Field label="Model" hint="The part number on the body, e.g. HTI-96-MIN.">
            <input value={type} onChange={(e) => setType(e.target.value)} placeholder="HTI-96-MIN" />
          </Field>
          <Field label="Serial" hint="What is printed on the label. This is how a person identifies it in a box of five.">
            <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="SN-1785249784" />
          </Field>
          <Field label="Depth rating (m)" hint="From the datasheet. Not enforced anywhere; it is here so nobody has to look it up on a boat.">
            <input type="number" step="1" value={depth} onChange={(e) => setDepth(e.target.value)} />
          </Field>
          <Field label="Notes">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Cable length, quirks, where it lives" />
          </Field>
          {err && <div className="notice bad">{err}</div>}
          <div className="row">
            <button className="btn sm" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
